import React, { useContext, useState } from 'react';

// MUI
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import PrintIcon from '@material-ui/icons/Print';

// actions
import { addToQueueWithQuantity } from '../../../Actions';

// dependents
import { AlertContext } from '../../../Context/AlertContextProvider';
import { PrintQueueContext } from '../../../Context/PrintQueueContextProvider';

// Matches the per-request cap in queueForPrintCopies.
const MAX_COPIES = 50;

const clamp = (n) => Math.min(Math.max(n, 1), MAX_COPIES);

/**
 * "Copies each − [n] + [Add N labels]" — the single place to queue QR labels for
 * the products currently checked in the results table.
 *
 * The count is deliberately labelled "copies each" and the button states the
 * computed total, because a bare number beside "3 selected" reads ambiguously
 * (3 labels? 3 each?). The button answers that before it's clicked.
 *
 * Held as a string so the field can be typed into directly (including transiently
 * empty); only clamped on blur/submit.
 */
const AddToPrintQueue = ({ productUuids }) => {
  const { handleAlertOpen, setMessageType, setMessage } =
    useContext(AlertContext);
  const { setUpdate } = useContext(PrintQueueContext);
  const [quantity, setQuantity] = useState('1');
  const [busy, setBusy] = useState(false);

  // What we'd actually send: an empty or half-typed field counts as 1.
  const copies = clamp(parseInt(quantity, 10) || 1);
  const totalLabels = copies * productUuids.length;

  const step = (delta) => setQuantity(String(clamp(copies + delta)));

  const handleTyping = (value) => {
    if (value === '') return setQuantity('');
    if (!/^\d{1,2}$/.test(value)) return;
    setQuantity(value);
  };

  // Snap a typed value into range once the user leaves the field, so what's on
  // screen always matches what would be queued.
  const handleBlur = () => setQuantity(String(copies));

  const handleAdd = async () => {
    setBusy(true);
    try {
      await addToQueueWithQuantity(productUuids, copies);
      setUpdate(true);
      setMessageType('success');
      setMessage(
        totalLabels === 1
          ? 'Added 1 label to print queue'
          : `Added ${totalLabels} labels to print queue`
      );
      setQuantity('1');
    } catch (err) {
      setMessageType('error');
      setMessage('Error adding to print queue');
    } finally {
      setBusy(false);
      handleAlertOpen();
    }
  };

  return (
    <div className="queue-add">
      <span className="queue-add__label">Copies each</span>
      <IconButton
        size="small"
        aria-label="one fewer copy of each product"
        className="queue-add__step"
        disabled={busy || copies <= 1}
        onClick={() => step(-1)}
      >
        <RemoveIcon fontSize="inherit" />
      </IconButton>
      <input
        type="text"
        inputMode="numeric"
        className="queue-add__count"
        aria-label="copies of each product"
        value={quantity}
        disabled={busy}
        onChange={(e) => handleTyping(e.target.value)}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleAdd();
        }}
      />
      <IconButton
        size="small"
        aria-label="one more copy of each product"
        className="queue-add__step"
        disabled={busy || copies >= MAX_COPIES}
        onClick={() => step(1)}
      >
        <AddIcon fontSize="inherit" />
      </IconButton>
      <Tooltip
        title={
          copies === 1
            ? `Queue one QR label for each of the ${productUuids.length} selected products`
            : `Queue ${copies} QR labels for each of the ${productUuids.length} selected products`
        }
      >
        <Button
          variant="contained"
          className="queue-add__btn"
          startIcon={<PrintIcon />}
          disabled={busy || !productUuids.length}
          onClick={handleAdd}
        >
          {busy
            ? 'Adding…'
            : `Add ${totalLabels} label${totalLabels === 1 ? '' : 's'}`}
        </Button>
      </Tooltip>
    </div>
  );
};

export default AddToPrintQueue;
